import { z } from 'zod'

import { EmploymentEquipmentType } from 'src/graphql/types/employmentExperiences'
import { removeCountryCode, unformatPhoneNumber } from 'src/lib/phone.utils'

// Employment Experience validation schema
export const EmploymentExperienceSchema = z.object({
  companyName: z
    .string()
    .min(1, { message: 'Company name is required' })
    .max(100),
  position: z.string().min(1, { message: 'Position is required' }).max(100),
  unemploymentReason: z.string().optional().nullable().or(z.literal('')),
  startDate: z.date({ invalid_type_error: 'Start date is required' }),
  endDate: z.date().optional().or(z.literal(null)),
  dotNumber: z
    .string()
    .nullable()
    .optional()
    .or(z.literal(''))
    .refine((data) => data === null || data === '' || /^\d+$/.test(data), {
      message: 'DOT number must contain only numbers',
    }),
  contactName: z.string().optional().nullable().or(z.literal('')),
  email: z
    .string()
    .email({ message: 'Invalid email' })
    .optional()
    .nullable()
    .or(z.literal('')),
  phone: z
    .string()
    .refine(
      (data) =>
        unformatPhoneNumber(removeCountryCode(data)).length === 10 ||
        unformatPhoneNumber(removeCountryCode(data)).length === 0,
      {
        message: 'Phone number should have 10 digits',
      }
    )
    .optional()
    .nullable()
    .or(z.literal('')),
  faxNumber: z
    .string()
    .refine(
      (data) =>
        unformatPhoneNumber(removeCountryCode(data)).length === 10 ||
        unformatPhoneNumber(removeCountryCode(data)).length === 0,
      {
        message: 'Fax number should have 10 digits',
      }
    )
    .optional()
    .nullable()
    .or(z.literal('')),
  reasonForLeaving: z.string().optional().or(z.literal('')),
  equipmentTypes: z
    .array(z.nativeEnum(EmploymentEquipmentType))
    .optional()
    .nullable(),
  address: z
    .object({
      street: z.string().optional().nullable().or(z.literal('')),
      city: z.string().optional().nullable().or(z.literal('')),
      state: z.string().optional().nullable().or(z.literal('')),
      zipCode: z.string().optional().nullable().or(z.literal('')),
      country: z.string().optional().nullable().or(z.literal('')),
    })
    .optional()
    .nullable(),
  isDriverTerminated: z.boolean().optional().nullable(),
  isDriverSubjectToFMCSRs: z.boolean().optional().nullable(),
  isJobDesignatedAsSafetySensitiveFunctionUnderDOT: z
    .boolean()
    .optional()
    .nullable(),
  shouldContactEmployer: z.boolean().optional().nullable(),
}).refine(
  (data) =>
    data.endDate == null ||
    (!!data.reasonForLeaving && data.reasonForLeaving.trim().length > 0),
  {
    message: 'Reason for leaving is required',
    path: ['reasonForLeaving'],
  }
)

// Unemployment Experience validation schema
export const UnemploymentExperienceSchema = z.object({
  startDate: z.date({ invalid_type_error: 'Start date is required' }),
  endDate: z.date().optional().nullable().or(z.literal(null)),
  unemploymentReason: z
    .string()
    .min(1, { message: 'Unemployment reason is required' }),
})

// Type inference for the schemas
export type EmploymentExperienceFormData = z.infer<
  typeof EmploymentExperienceSchema
>
export type UnemploymentExperienceFormData = z.infer<
  typeof UnemploymentExperienceSchema
>
